// Login Page Component

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card, CardBody } from '../components/common';
import { isServerHostedPath, pickPostLoginDestination } from '../utils/landingPage';
import { loginErrorMessage } from '../utils/loginErrorMessage';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');

  // Explicit return URL from query param or router state (set by ProtectedRoute)
  const returnUrl = searchParams.get('returnUrl');
  const stateFrom = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const explicitDestination = returnUrl || stateFrom;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    // The api treats the login email case-insensitively; send the canonical
    // form so a stray leading/trailing space or Shift-key never blocks sign-in.
    const loginEmail = email.trim().toLowerCase();

    if (!loginEmail || !password) {
      setFormError('Please enter both email and password');
      return;
    }

    try {
      const user = await login({ email: loginEmail, password });
      // Forced destinations (e.g. cplex@ operator) win over an explicit
      // returnUrl; otherwise honor the page the user was redirected from
      // and fall back to their scope-appropriate landing page.
      const destination = pickPostLoginDestination(user, explicitDestination);
      if (isServerHostedPath(destination)) {
        // Served by the api, not the React router (e.g. /kb-explorer/): the
        // cm_auth cookie login just set rides along on a full navigation.
        window.location.assign(destination);
        return;
      }
      navigate(destination, { replace: true });
    } catch (err) {
      // A 401 is a wrong password; a 5xx, timeout or network failure is an
      // outage and must not be dressed up as one (kanban d2f93d2e).
      setFormError(loginErrorMessage(err));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginBox}>
        {/* Logo */}
        <div className={styles.logoSection}>
          <img
            src="/chemmasters-logo.png"
            alt="ChemMasters"
            className={styles.logo}
          />
        </div>

        {/* Login Form */}
        <Card padding="lg">
          <CardBody>
            <form onSubmit={handleSubmit} className={styles.form}>
              <h2 className={styles.formTitle}>Sign In</h2>

              {(formError || error) && (
                <div className={styles.error} role="alert">
                  {formError || error}
                </div>
              )}

              <Input
                label="Email"
                type="email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
              />

              <Input
                label="Password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isLoading}
              >
                Sign In
              </Button>
            </form>
          </CardBody>
        </Card>

        <p className={styles.helpText}>
          <Link to="/forgot-password" className={styles.helpLink} data-action="login_forgot_password">
            Forgot your password?
          </Link>
        </p>
        <p className={styles.helpText}>
          Contact your administrator if you need access.
        </p>
      </div>
    </div>
  );
}
